// 🎬 «쇼릴형» 무대 토큰·순수 계산(2026-10-02 · 2-1) — 어두운 바탕 · 원색 판 · 띠 쓸림 컷.
//   정본 지도 `docs/01-plan/features/motion-style-pack.md` §12-13 · 그리는 곳 `ShowreelStage.tsx`.
//   순수: 같은 입력 = 같은 값. 시계·프레임 간 상태 없음.
import { briefContrast, briefMixHex, briefTextOn } from './motion-brief-shape';

/** 바탕(먹) · 짙은 판 · 격자선. */
export const REEL_BG = '#0B0D12';
export const REEL_PANEL = '#151922';
export const REEL_GRID = 'rgba(255,255,255,0.045)';
/** 격자 한 칸(1920 기준 px). */
export const REEL_GRID_PX = 120;
/** 판 모서리 상한(1920 기준 px) — 모션 브리핑의 둥근 도형(최대 64)과 달리 각진 판. */
export const REEL_RADIUS_MAX = 10;

/** 판 색 종류 — 제목 장면(여닫이)·큰 숫자 = 원색 판 · 나머지 = 짙은 판. */
export type ReelFill = 'brand' | 'panel';
const BRAND_TYPES: ReadonlySet<string> = new Set(['fullText', 'kineticTypo', 'bigNumber']);
export const reelFillOf = (type: string): ReelFill => (BRAND_TYPES.has(type) ? 'brand' : 'panel');
export const reelFillColor = (fill: ReelFill, brand: string): string => (fill === 'brand' ? brand : REEL_PANEL);

/**
 * 어두운 판 위 강조색 — 판과 대비 3:1 미만이면 흰색 쪽으로 밝힌다(색상은 유지 · 모션 브리핑 `normalizeAccent` 의 반대 방향).
 *   10% 씩 최대 9번 — 그래도 안 되면 흰색.
 */
export function reelAccentOnDark(accent: string, bg: string = REEL_PANEL): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(accent)) return '#FFFFFF';
  for (let i = 0; i <= 9; i++) {
    const c = i === 0 ? accent : briefMixHex(accent, '#FFFFFF', i / 10);
    if (briefContrast(c, bg) >= 3) return c;
  }
  return '#FFFFFF';
}

/** 판 색에 맞춘 장표 글자색 — 짙은 판 = 흰 글자 + 밝힌 강조색(원래 브랜드색에서 · 흰 바탕용으로 어둡게 보정된 `base.accent` 가 아니다) · 원색 판 = 흰/먹 중 대비 큰 쪽(강조도 같은 색). */
export function reelContentColors<C extends { textMain?: string; textSub?: string; accent: string; isDark?: boolean }>(fill: ReelFill, base: C, brand: string): C {
  if (fill === 'panel') {
    return { ...base, textMain: '#FFFFFF', textSub: 'rgba(255,255,255,0.78)', accent: reelAccentOnDark(brand), isDark: true };
  }
  const text = briefTextOn(brand);
  const light = text === '#FFFFFF';
  return { ...base, textMain: text, textSub: light ? 'rgba(255,255,255,0.84)' : 'rgba(17,24,39,0.80)', accent: text, isDark: light };
}

/* ── 띠 쓸림 컷 ─────────────────────────────────────────────── */

/** 쓸림 길이(초) — 원색 띠가 왼쪽 밖에서 오른쪽 밖까지 지나간다. */
export const REEL_WIPE_SEC = 0.4;
/** 띠 폭(화면 폭 비율) — 한순간에 덮는 면적의 상한. 넓히면 밝은 브랜드색에서 프레임 간 밝기 차가 커진다(급변 기준 25.5). */
export const REEL_BAND_W = 0.1;
/** 띠 뒤를 따라가는 얇은 흰 선(화면 폭 비율). */
export const REEL_BAND_TRAIL_W = 0.006;

const smooth = (x: number): number => { const c = x < 0 ? 0 : x > 1 ? 1 : x; return c * c * (3 - 2 * c); };

/**
 * 이 시각의 쓸림 — `edge` = 새 장면이 드러난 오른쪽 끝(px · 띠의 뒤꽁무니). 띠는 [edge, edge + bandW].
 *   0초: edge = −bandW(띠가 화면 왼쪽 밖) → 끝: edge = W(다 드러남 · 띠는 화면 오른쪽 밖). 끝난 뒤는 null.
 */
export function reelWipe(tSec: number, W: number): { edge: number; bandW: number; p: number } | null {
  if (!(tSec >= 0) || tSec >= REEL_WIPE_SEC) return null;
  const bandW = Math.round(W * REEL_BAND_W);
  const p = smooth(tSec / REEL_WIPE_SEC);
  return { edge: Math.round(-bandW + p * (W + bandW)), bandW, p };
}

/* ── 제목 «쾅» ─────────────────────────────────────────────── */

/** 제목이 들어올 때의 확대 시작 배율과 길이(초) — 1 로 내려앉고 넘침(바운스) 없음. */
export const REEL_SLAM_SCALE = 1.12;
export const REEL_SLAM_SEC = 5 / 30;
/** 이 시각(등장 시작 기준 초)의 제목 배율 — 등장 전·끝난 뒤는 1. */
export function reelSlamScale(tSinceSec: number, cap: number = REEL_SLAM_SCALE): number {
  if (!(tSinceSec >= 0) || tSinceSec >= REEL_SLAM_SEC) return 1;
  const q = 1 - tSinceSec / REEL_SLAM_SEC;
  return 1 + (Math.max(1, Math.min(REEL_SLAM_SCALE, cap)) - 1) * q * q * q;
}
/**
 * 이 제목 띠가 캔버스 안에서 커질 수 있는 최대 배율(띠 가운데 기준 · 띠 폭 전체를 글자로 본 넉넉한 쪽) — 1.12 를 넘지 않는다.
 *   긴 제목은 띠가 글자 자리 폭을 꽉 채워 1.12 배면 캔버스 밖으로 나간다(«제목 글자는 한 글자도 안 잘린다») → 들어갈 만큼만 키운다.
 */
export function reelSlamCap(band: { left: number; top: number; width: number; height: number }, W: number, H: number): number {
  const cx = band.left + band.width / 2, cy = band.top + band.height / 2;
  const fitX = band.width > 0 ? Math.min(cx, W - cx) / (band.width / 2) : REEL_SLAM_SCALE;
  const fitY = band.height > 0 ? Math.min(cy, H - cy) / (band.height / 2) : REEL_SLAM_SCALE;
  return Math.max(1, Math.min(REEL_SLAM_SCALE, fitX, fitY));
}
/**
 * 확대 최대 시점의 제목 상자(띠 가운데 기준 확대) — «제목 글자는 한 글자도 안 잘린다» 불변식이 이 상자를 캔버스와 견준다.
 *   `textW` = 가장 긴 줄의 글자 폭(px) · 띠 가운데 정렬.
 */
export function reelSlamBox(band: { left: number; top: number; width: number; height: number }, textW: number, scale: number = REEL_SLAM_SCALE): { l: number; t: number; r: number; b: number } {
  const cx = band.left + band.width / 2, cy = band.top + band.height / 2;
  const hw = (Math.min(textW, band.width) / 2) * scale, hh = (band.height / 2) * scale;
  return { l: cx - hw, t: cy - hh, r: cx + hw, b: cy + hh };
}

/* ── 3D 층 ─────────────────────────────────────────────────── */

/** 구슬 샌드위치(글자 뒤·앞)를 얹는 장표 종류 — 이 가운데 첫 장면과 큰 숫자에만 실제로 얹는다(무대 · 렌더 시간 예산 +30%). */
export const REEL_ORB_TYPES: ReadonlySet<string> = new Set(['fullText', 'kineticTypo', 'bigNumber']);
